import './app-chrome.css';

import { STREAMLAIN_CORE_LOGO } from '@affine/component/brand';
import { AppThemeService } from '@affine/core/modules/theme';
import { THEME_PRESETS } from '@affine/core/modules/theme-presets';
import { useService } from '@toeverything/infra';
import { ThemeProvider as NextThemeProvider, useTheme } from 'next-themes';
import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';

import {
  getNextAppearanceTheme,
  isAppearanceThemeRotationShortcut,
} from './appearance-theme-shortcut';

// Include standard themes and all custom presets
const themes = ['dark', 'light', ...Object.keys(THEME_PRESETS)];
const BOOT_THEME_STORAGE_KEY = 'streamlain:boot-theme';
const BOOT_LIGHT_BACKGROUND = '#F7F4ED';
const BOOT_DARK_BACKGROUND = '#141414';

function getBootThemeName(
  theme: string | undefined,
  resolvedTheme: string | undefined
): string | undefined {
  return theme === 'system' ? resolvedTheme : (theme ?? resolvedTheme);
}

function readPresetThemeVariable(
  bootTheme: string | undefined,
  variableName: string
): string | null {
  if (!bootTheme) {
    return null;
  }

  return THEME_PRESETS[bootTheme]?.[variableName] ?? null;
}

function resolveBootBackground(bootTheme: string | undefined): string {
  return (
    readPresetThemeVariable(
      bootTheme,
      '--streamlain-v2-layer-background-primary'
    ) ??
    readPresetThemeVariable(
      bootTheme,
      '--streamlain-background-primary-color'
    ) ??
    (bootTheme === 'dark' ? BOOT_DARK_BACKGROUND : BOOT_LIGHT_BACKGROUND)
  );
}

function writeBootThemePayload(
  theme: string | undefined,
  resolvedTheme: string | undefined
): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const bootTheme = getBootThemeName(theme, resolvedTheme);
    const primary =
      readPresetThemeVariable(bootTheme, '--streamlain-v2-button-primary') ??
      readPresetThemeVariable(bootTheme, '--streamlain-primary-color') ??
      STREAMLAIN_CORE_LOGO.primary;
    const background = resolveBootBackground(bootTheme);
    const art = STREAMLAIN_CORE_LOGO.primary;
    const grid =
      bootTheme === 'dark' || bootTheme === 'blue-white'
        ? '140 190 240'
        : '80 144 211';

    localStorage.setItem(
      BOOT_THEME_STORAGE_KEY,
      JSON.stringify({
        art,
        background,
        grid,
        primary,
        sourceTheme: theme,
        theme: bootTheme,
      })
    );
  } catch {
    // Boot theme is a visual cache only.
  }
}

function ThemeObserver() {
  const { resolvedTheme, setTheme, theme } = useTheme();
  const service = useService(AppThemeService);

  useEffect(() => {
    service.appTheme.theme$.next(resolvedTheme);
    writeBootThemePayload(theme, resolvedTheme);
  }, [resolvedTheme, service.appTheme.theme$, theme]);

  useEffect(() => {
    const rotateAppearanceTheme = (event: KeyboardEvent) => {
      if (!isAppearanceThemeRotationShortcut(event)) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      setTheme(getNextAppearanceTheme(theme, resolvedTheme));
    };

    window.addEventListener('keydown', rotateAppearanceTheme, true);
    return () => {
      window.removeEventListener('keydown', rotateAppearanceTheme, true);
    };
  }, [resolvedTheme, setTheme, theme]);

  return null;
}

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  return (
    <NextThemeProvider
      themes={themes}
      defaultTheme="system"
      enableSystem={true}
    >
      {children}
      <ThemeObserver />
    </NextThemeProvider>
  );
};
